import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { openFigmaConnectPopup } from '@/services/auth/firebase/oidc-providers/figma-oidc-provider';
import { useGlobalStore } from '@/state/global.state';
import { I18N_KEYS } from '@/language/keys';
import { SettingsPageSections } from '@/enums/settings-page-sections.enum';
import { color } from '@/colors';
import { LinkIcon } from '@/UI/Icons';
import {
  FIGMA_OAUTH_CALLBACK_CHANNEL,
  FIGMA_OAUTH_CALLBACK_MESSAGE_TYPE,
  type FigmaOAuthCallbackMessage,
} from '@/pages/FigmaOAuthCallback';
import { useConnectFigmaAccountMutation, type FigmaConnection } from '@/hooks/useLinkedFigmaAccount';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import {
  getCommunityPublishErrorPayload,
  type WorkflowCommunityPublishTrackingContext,
} from '@/components/Recipe/FlowComponents/Share/workflowCommunityPublishAnalytics';
import type { SnackbarData } from '@/state/global.state';

type LinkResult = { code: string } | { canceled: true } | { error: string };

const LINK_TIMEOUT_MS = 5 * 60 * 1000;

export const useLinkFigmaAccount = (
  onLinked?: (connection: FigmaConnection) => void,
  trackingContext?: WorkflowCommunityPublishTrackingContext,
) => {
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const successSnackbar = useMemo<Partial<SnackbarData>>(
    () => ({
      icon: <LinkIcon width={16} height={16} style={{ color: color.Black100 }} />,
      action: {
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.MANAGE_IN_SETTINGS),
        onClick: () => navigate(`/settings?section=${SettingsPageSections.PROFILE}`),
        mode: 'text',
      },
      minWidth: 400,
    }),
    [t, navigate],
  );
  const { mutateAsync: connectFigmaAccount } = useConnectFigmaAccountMutation({ successSnackbar });
  const [isLinking, setIsLinking] = useState(false);
  const onLinkedRef = useRef(onLinked);
  // Refs, not deps: `track` changes with credits and callers may pass an inline context, and either
  // would give `link` a new identity.
  const trackRef = useRef(track);
  const trackingContextRef = useRef(trackingContext);

  useEffect(() => {
    onLinkedRef.current = onLinked;
    trackRef.current = track;
    trackingContextRef.current = trackingContext;
  }, [onLinked, track, trackingContext]);

  const trackLinkResult = useCallback(
    (
      context: WorkflowCommunityPublishTrackingContext | undefined,
      result: 'success' | 'failure' | 'canceled',
      error?: unknown,
    ) => {
      if (!context) return;
      trackRef.current(
        WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.FIGMA_LINK_RESULT,
        {
          ...context,
          result,
          ...(error ? getCommunityPublishErrorPayload(error) : {}),
        },
        TrackTypeEnum.Product,
      );
    },
    [],
  );

  // Stable across renders: callers put it in effect deps, and a new `link` every render looped
  // Flow's renders (WEA-7429).
  const link = useCallback(async () => {
    // Report the result against the context the user clicked in, not one that changed while the popup was open.
    const clickContext = trackingContextRef.current;
    setIsLinking(true);
    try {
      const { popup, state, redirectUri } = await openFigmaConnectPopup();

      const result = await new Promise<LinkResult>((resolve) => {
        const channel = new BroadcastChannel(FIGMA_OAUTH_CALLBACK_CHANNEL);
        const cleanup = () => {
          channel.close();
          clearInterval(closedPoll);
          clearTimeout(timeout);
        };
        channel.onmessage = (event: MessageEvent<FigmaOAuthCallbackMessage>) => {
          const data = event.data;
          if (data?.type !== FIGMA_OAUTH_CALLBACK_MESSAGE_TYPE) return;
          if (data.state !== state) return;
          cleanup();
          if (data.error) resolve({ error: data.error });
          else if (data.code) resolve({ code: data.code });
          else resolve({ canceled: true });
        };
        // User closed the popup before completing — silent cancel, no error.
        const closedPoll = setInterval(() => {
          if (popup.closed) {
            cleanup();
            resolve({ canceled: true });
          }
        }, 500);
        // Safety net for popups left open with no callback message.
        const timeout = setTimeout(() => {
          cleanup();
          if (!popup.closed) popup.close();
          resolve({ canceled: true });
        }, LINK_TIMEOUT_MS);
      });

      if ('canceled' in result) {
        trackLinkResult(clickContext, 'canceled');
        return;
      }
      if ('error' in result) throw new Error(result.error);

      const data = await connectFigmaAccount({ code: result.code, redirectUri }).catch(() => null);
      if (data) {
        trackLinkResult(clickContext, 'success');
        onLinkedRef.current?.(data);
      } else {
        trackLinkResult(clickContext, 'failure');
      }
    } catch (error) {
      trackLinkResult(clickContext, 'failure', error);
      updateSnackbarData({
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.LINK_FAILURE),
        isOpen: true,
      });
    } finally {
      setIsLinking(false);
    }
  }, [connectFigmaAccount, t, trackLinkResult, updateSnackbarData]);

  return { link, isLinking };
};
